import { AuthRequest, AuthResponse } from '../models/auth.model';
import { BranchOffice } from '../models/branch-office.model';
import { User } from '../models/user.model';
import { apiService } from './api.service';

export interface LoginCandidate {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string | null;
  roleName?: string | null;
  branchHint?: string | null;
  lastLoginAt?: string | null;
}

export interface AuthMultiCandidateResponse {
  kind: 'multiple_candidates';
  loginToken: string;
  candidates: LoginCandidate[];
}

export type LoginResult = AuthResponse | AuthMultiCandidateResponse;

export const isMultiCandidateLogin = (
  result: LoginResult
): result is AuthMultiCandidateResponse =>
  (result as AuthMultiCandidateResponse).kind === 'multiple_candidates';

export const loginAuthService = (login: AuthRequest): Promise<LoginResult> => {
  return apiService.getInstance().post('/auth/login', login);
};

/**
 * Finalise a multi-business login: posts the short-lived loginToken +
 * chosen businessId + the original password back to the server, which
 * re-verifies and issues the real JWT scoped to that business.
 */
export const loginSelectService = (data: {
  loginToken: string;
  businessId: string;
  password: string;
}): Promise<AuthResponse> => {
  return apiService.getInstance().post('/auth/login/select', data);
};

// Onboarding types
export interface CompleteOnboardingRequest {
  onboardingToken: string;
  // Only sent for Google-registered accounts (they set their first real
  // password here). Email/password registrations omit this field — the
  // password from /auth/register is reused.
  password?: string;
  business: {
    name: string;
    businessType: string;
    monthlyInvoiceVolume?: string;
  };
  adminUser: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  emailLabels?: {
    subject?: string;
    greeting?: string;
    accountCreated?: string;
    goToDashboard?: string;
    disclaimer?: string;
  };
  couponCode?: string;
}

export interface OnboardingCompletionResponse {
  accessToken: string;
  refreshToken: string;
  user: User & { branchOffice?: BranchOffice };
  businessId: string;
  businessName: string;
  adminUserId: string;
  adminUsername: string;
  adminEmail: string;
  cashierUserId: string;
  cashierUsername: string;
  branchId: string;
  branchName: string;
  warehouseId: string;
  warehouseName: string;
  message: string;
}

export interface OnboardingTokenValidationResponse {
  accountId: string;
  email: string;
  country: string;
  plan: string;
  isGoogleUser: boolean;
  firstName?: string;
  lastName?: string;
}

export const completeOnboardingService = (
  data: CompleteOnboardingRequest
): Promise<OnboardingCompletionResponse> => {
  // Onboarding does a lot of synchronous work (business + branch +
  // tax seed + roles + permissions + admin user) so the default 15s
  // ceiling has been timing out on slower Supabase windows. Bump
  // this single call to 45s — long enough to let the path finish
  // even on a cold pooler, short enough that we still surface real
  // hangs to the UI.
  return apiService.getInstance().post('/auth/complete-onboarding', data);
};

export const validateOnboardingTokenService = (
  token: string
): Promise<OnboardingTokenValidationResponse> => {
  return apiService.getInstance().get(`/auth/onboarding/${token}`);
};

// Forgot Password types
export interface ForgotPasswordRequest {
  email: string;
  /**
   * Cloudflare Turnstile token. Always required when the server has
   * `TURNSTILE_SECRET_KEY` configured — forgot-password is rate-limited
   * but unauthenticated, so a captcha gate keeps it from being abused
   * to spam reset emails to arbitrary inboxes.
   */
  captchaToken?: string;
  emailLabels?: {
    subject?: string;
    title?: string;
    greeting?: string;
    body?: string;
    buttonText?: string;
    linkInstruction?: string;
    disclaimer?: string;
  };
}

export interface ForgotPasswordResponse {
  message: string;
  locked?: boolean;
}

export const forgotPasswordService = (
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  return apiService.getInstance().post('/auth/forgot-password', data);
};

// Reset Password types
export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export const resetPasswordService = (
  data: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  return apiService.getInstance().post('/auth/reset-password', data);
};

// Refresh token services
export const refreshTokenService = (
  refreshToken: string
): Promise<AuthResponse> => {
  return apiService.getInstance().post('/auth/refresh', { refreshToken });
};

export const logoutService = (
  refreshToken: string
): Promise<{ message: string }> => {
  return apiService.getInstance().post('/auth/logout', { refreshToken });
};

export const logoutAllService = (): Promise<{
  message: string;
  revokedSessions: number;
}> => {
  return apiService.getInstance().post('/auth/logout-all');
};

/**
 * Card-based login — POST { cardUid } returns the same AuthResponse the
 * password / PIN flows return so the FE auth store hydrates identically.
 */
export const loginWithCardService = (
  cardUid: string
): Promise<AuthResponse> => {
  return apiService.getInstance().post('/auth/card/login', { cardUid });
};

/**
 * Card-based unlock from the lock screen. Reuses the existing
 * /auth/card/login endpoint and validates the returned user id
 * matches the currently locked session — refuses to unlock when
 * a different user's card is tapped.
 */
export const verifyCardForUnlockService = async (
  cardUid: string,
  expectedUserId: string | undefined
): Promise<{ valid: boolean }> => {
  if (!expectedUserId) return { valid: false };
  try {
    const res = (await apiService
      .getInstance()
      .post('/auth/card/login', { cardUid })) as AuthResponse;
    return { valid: res.user?.id === expectedUserId };
  } catch {
    return { valid: false };
  }
};
